import { useEffect, useState, useContext, useMemo, useCallback } from "react";
import CustomerService from "services/CustomerService";
import CustomerHelper from "helpers/CustomerHelper";
import { useParams, useNavigate } from "react-router-dom";
import styles from "./styles.module.css";
import {
  Grid,
  InputAdornment,
  IconButton,
  Paper,
  TablePagination,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from "@mui/material";
import DataTable from "components/utils/DataTable";
import Info from "components/utils/Alert/Info";
import CustomerSaleDialog from "components/popups/CustomerSaleDialog";
import DateTimeHelpers from "helpers/DateTimeHelpers";
import AddPaymentToCustomerDialog from "components/popups/AddPaymentToCustomer/AddPaymentToCustomer";
import SearchIcon from "images/svg/search.svg";
import { ReactComponent as AddPayment } from "images/svg/addPayment.svg";
import { ReactComponent as EditIcon } from "images/svg/edit-2.svg";
import { ReactComponent as SyncIcon } from "images/svg/refresh-2.svg";
import EditCustomer from "components/popups/EditCustomer";
import CustomerContext from "context/CustomerContext";
import { ReactComponent as ArrowIcon } from "images/svg/bold-arrow.svg";
import CustomDateRangePicker from "components/utils/CustomFields/CustomDateRangePicker";
import SkeletonLoading from "components/utils/Loading/SkeletonLoading";
import * as NumberFormatHelper from "helpers/NumberFormatHelper";
import { SALE_SYNC_STATUS, ROUTES, ROUTE_PREFIXES } from "const";
import PrinterImg from "images/svg/printer.svg";
import ShareIcon from "images/svg/share.svg";
import ShopContext from "context/ShopContext";
import * as ReportHelper from "helpers/ReportHelper";
import BackIcon from "images/svg/back-icon.svg";
import toast from "react-hot-toast";
import * as InvoiceActionHelper from "helpers/InvoiceActionHelper";

function CustomerDetails() {
  const navigate = useNavigate();
  const { updateCustomer, fetchCustomers, addPaymentToCustomer } = useContext(CustomerContext);
  const { selectedShop } = useContext(ShopContext);

  const [loading, setloading] = useState(false);
  const [customerJournals, setCustomerJournals] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [filterText, setFilterText] = useState("");
  const [customerJournalsPagination, setCustomerJournalsPagination] = useState({
    page: 0,
    limit: 20,
  });
  const [dateRange, setDateRange] = useState([new Date(new Date().setDate(new Date().getDate() - 7)), new Date()]);
  const [selectedSaleId, setSelectedSaleId] = useState(null);
  const [paymentTab, setPaymentTab] = useState(false);
  const [editCustomer, setEditCustomer] = useState(false);
  const [upcomingBalance, setUpcomingBalance] = useState(0);
  const [hasUnsyncedLogs, setHasUnsyncedLogs] = useState(false);
  const [syncedBalance, setSyncedBalance] = useState(0);
  const { customerId: customerIdParam } = useParams();

  const isLocalParam = customerIdParam?.startsWith(ROUTE_PREFIXES.LOCAL_CUSTOMER);
  const initialCustomerId = isLocalParam ? null : customerIdParam ? Number(customerIdParam) : null;

  const initialCustomerLocalId = isLocalParam
    ? Number(customerIdParam.substring(ROUTE_PREFIXES.LOCAL_CUSTOMER.length))
    : null;

  const [customerLocalId, setCustomerLocalId] = useState(initialCustomerLocalId);
  const [customerId, setCustomerId] = useState(initialCustomerId);

  useEffect(() => {
    if (customerIdParam?.startsWith(ROUTE_PREFIXES.LOCAL_CUSTOMER)) {
      setCustomerLocalId(Number(customerIdParam.substring(ROUTE_PREFIXES.LOCAL_CUSTOMER.length)));
      setCustomerId(null);
    } else if (customerIdParam) {
      setCustomerId(Number(customerIdParam));
      setCustomerLocalId(null);
    }
  }, [customerIdParam]);

  const filteredJournals = useMemo(() => {
    if (!customerJournals) return [];
    if (!filterText) return customerJournals;

    const lowerFilter = filterText.toLowerCase();

    return customerJournals.filter((item) => {
      const date = new Date(item.time);
      const formattedDate = DateTimeHelpers.convertDateToUtcDmy(date);

      const match = item.description?.match(/"([^"]+)"/);
      const displayDesc = (match ? match[1] : item.description || "").toLowerCase();

      return formattedDate.toLowerCase().includes(lowerFilter) || displayDesc.includes(lowerFilter);
    });
  }, [customerJournals, filterText]);

  const paginatedJournals = useMemo(() => {
    const start = customerJournalsPagination.page * customerJournalsPagination.limit;
    return filteredJournals.slice(start, start + customerJournalsPagination.limit);
  }, [filteredJournals, customerJournalsPagination.page, customerJournalsPagination.limit]);

  const getBalance = (data) => {
    const balance = Number(data?.balance) || 0;
    const balanceLabel = NumberFormatHelper.customerAmountFormat(balance);
    const balanceStyle = balance >= 0 ? { color: "green" } : { color: "red" };
    return (
      <Typography variant="body2" style={balanceStyle} gutterBottom>
        {balanceLabel}
      </Typography>
    );
  };

  const headerData = [
    {
      label: "Date",
      id: "Date",
      type: "callback",
      width: "150px",
      viewRender: (item) => {
        const date = new Date(item.time);
        return DateTimeHelpers.convertDateToUtcDmy(date);
      },
    },
    {
      label: "Time",
      id: "time",
      type: "callback",
      width: "150px",
      viewRender: (item) => {
        const date = item.time.split("T")[1].split(".")[0];
        return DateTimeHelpers.formatServerTimeSegment(date);
      },
    },
    {
      label: "Description",
      id: "description",
      type: "callback",
      viewRender: (item) => {
        const match = item.description?.match(/"([^"]+)"/);
        const desc = match ? match[1] : item.description || "-";
        if (item.sync_status && item.sync_status !== SALE_SYNC_STATUS.SYNCED_TO_CLOUD) {
          const displayStatus =
            item.sync_status === SALE_SYNC_STATUS.FAILED ? SALE_SYNC_STATUS.UNSYNCED : item.sync_status;
          return (
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span>{desc}</span>
              <span
                style={{
                  fontSize: "10px",
                  fontWeight: "bold",
                  backgroundColor: item.sync_status === SALE_SYNC_STATUS.FAILED ? "#f44336" : "#ff9800",
                  color: "#fff",
                  padding: "2px 6px",
                  borderRadius: "4px",
                  textTransform: "uppercase",
                }}
              >
                {displayStatus}
              </span>
            </div>
          );
        }
        return desc;
      },
    },
    {
      label: "Credit",
      id: "credit",
      type: "text",
      width: "150px",
    },
    {
      label: "Debit",
      id: "debit",
      type: "text",
      width: "150px",
    },
    {
      label: "Balance",
      id: "balance",
      type: "callback",
      width: "150px",
      viewRender: (data) => getBalance(data),
    },
  ];

  const getCustomerJournals = useCallback(async () => {
    const activeCustomerId = customerId || customerLocalId;
    if (!activeCustomerId) return;
    setloading(true);

    const { serverJournals, isOfflineMode } = await CustomerHelper.fetchServerCustomerJournals(
      customerId,
      dateRange[0],
      dateRange[1],
    );

    try {
      const { finalUpcomingBalance, unsyncedLogs, syncedBalance, combinedJournals } =
        await CustomerHelper.computeCustomerUnsyncedJournals(activeCustomerId, serverJournals, isOfflineMode);

      setUpcomingBalance(finalUpcomingBalance);
      setHasUnsyncedLogs(unsyncedLogs);
      setSyncedBalance(syncedBalance);

      setCustomerJournals(combinedJournals);
    } catch (dbError) {
      console.error("Error processing offline customer logs:", dbError);
      setCustomerJournals(serverJournals);
    } finally {
      setloading(false);
    }
  }, [customerId, customerLocalId, dateRange]);

  const getCustomerDetails = useCallback(async () => {
    if (customerLocalId) {
      const localCustomer = await CustomerHelper.getCustomerByLocalIdFromDB(customerLocalId);
      if (localCustomer) {
        setCustomer(localCustomer);
      }
      return;
    }

    if (!customerId) return;

    try {
      const res = await CustomerService.getCustomer(customerId);
      if (res) {
        const saved = await CustomerHelper.saveOrUpdateCustomerOffline(res);
        setCustomer(saved);
      } else {
        setCustomer(res);
      }
    } catch (error) {
      console.log("Failed to fetch customer from server, loading from IndexedDB:", error.message);
      const localCustomer = await CustomerHelper.getCustomerByAnyIdFromDB(customerId, customerLocalId);
      if (localCustomer) {
        setCustomer(localCustomer);
      }
    }
  }, [customerId, customerLocalId]);

  const togglePaymentTab = () => setPaymentTab((prevValue) => !prevValue);
  const toggleEditCustomerTab = () => setEditCustomer((prevValue) => !prevValue);

  const toggleViewSale = (row) => {
    if (!row || (!row.sale_id && !row.sale_local_id)) return setSelectedSaleId(null);
    setSelectedSaleId(row.sale_id || row.sale_local_id);
  };

  const handlePrintCustomerDetails = async () => {
    if (!customer) return;
    try {
      const reportText = await ReportHelper.getCustomerReportThreeInch(
        customer,
        filteredJournals || [],
        selectedShop || {},
      );
      await ReportHelper.printReport({
        reportText,
        fallbackFunction: () => {
          window.print();
        },
      });
    } catch (error) {
      console.error("Failed to print customer statement:", error);
    }
  };

  const handleShareCustomerJournal = () => {
    if (!customer) return;
    try {
      InvoiceActionHelper.shareCustomerJournalOnWhatsApp(customer, filteredJournals || [], selectedShop || {});
    } catch (error) {
      toast.error(error?.message || "WhatsApp share failed: Customer mobile missing.");
    }
  };

  const addPaymentToCustomerHandler = async (payload) => {
    try {
      const result = await addPaymentToCustomer(customerId || customerLocalId, payload);

      setCustomerJournalsPagination((prev) => ({
        ...prev,
        page: 0,
      }));

      if (result && (result.id || result.local_id)) {
        setCustomer(result);
      } else {
        await getCustomerDetails();
      }
      await getCustomerJournals();
    } catch (error) {
      console.log(error.message);
      throw error;
    }
  };

  const handleChangeCustomerJournalPage = (event, newPage) => {
    setCustomerJournalsPagination((prev) => ({
      ...prev,
      page: newPage,
    }));
  };

  const handleChangeCustomerRowsPerPage = (event) => {
    const pageRowLimit = parseInt(event.target.value, 10);
    setCustomerJournalsPagination({
      limit: pageRowLimit,
      page: 0,
    });
  };

  const handleEditCustomer = async (updatedData) => {
    setloading(true);
    try {
      const updatedCustomer = await updateCustomer(customerId, updatedData);
      setEditCustomer(false);
      await fetchCustomers();

      if (updatedCustomer && (updatedCustomer.id || updatedCustomer.local_id)) {
        setCustomer(updatedCustomer);
      } else {
        await getCustomerDetails();
      }
    } catch (error) {
      console.error("Failed to update customer:", error.message);
    }
    setloading(false);
  };

  const handleReload = useCallback(() => {
    getCustomerDetails();
    getCustomerJournals();
  }, [getCustomerDetails, getCustomerJournals]);

  useEffect(() => {
    getCustomerDetails();
  }, [getCustomerDetails]);

  useEffect(() => {
    getCustomerJournals();
  }, [getCustomerJournals]);

  return (
    <>
      <Grid container className={styles.container} gap={"10px"}>
        <Grid item xs={12} className={styles.headerContainer}>
          <div className={styles.leftHeader}>
            <button
              type="button"
              className={styles.backButton}
              onClick={() => navigate(ROUTES.CUSTOMERS)}
              aria-label="Back to Customers"
            >
              <img src={BackIcon} alt="Back to Customers" />
            </button>

            <Grid className={styles.header}>
              <h2>Customer Details</h2>
              <h5>
                Customer <ArrowIcon style={{ margin: "0 4px" }} />
                {customer?.name}
              </h5>
            </Grid>
          </div>

          <Grid>
            <Grid item className={styles.contentWrapper}>
              <TextField
                placeholder="Search"
                size="small"
                className={styles.searchField}
                value={filterText}
                onChange={(event) => {
                  setFilterText(event.target.value);
                  setCustomerJournalsPagination((prev) => ({ ...prev, page: 0 }));
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="end">
                      <IconButton edge="end" style={{ padding: "0" }}>
                        <img src={SearchIcon} alt="Search" className={styles.icon} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <div className={styles.bar}> </div>

              <Grid className={styles.datePicker}>
                <CustomDateRangePicker
                  value={dateRange}
                  onChange={(newRange) => {
                    setDateRange(newRange);
                    setCustomerJournalsPagination((prev) => ({
                      ...prev,
                      page: 0,
                    }));
                  }}
                />
              </Grid>

              <div className={styles.bar}> </div>

              <div className={styles.actionButtons}>
                <Button
                  className={`${styles.button} ${styles.printBtn}`}
                  startIcon={<img src={PrinterImg} alt="Print" className={styles.iconSmall} />}
                  onClick={handlePrintCustomerDetails}
                >
                  Print
                </Button>

                <Button
                  className={`${styles.button} ${styles.printBtn}`}
                  startIcon={<img src={ShareIcon} alt="Share" className={styles.iconSmall} />}
                  onClick={handleShareCustomerJournal}
                >
                  Share
                </Button>

                <Button className={styles.addCustBtn} onClick={() => setEditCustomer(true)}>
                  <EditIcon className={styles.icon} />
                  Edit
                </Button>

                <Button className={styles.addCustBtn} onClick={togglePaymentTab}>
                  <AddPayment className={styles.icon} />
                  Add Payment
                </Button>
                <Button className={styles.addCustBtn} onClick={handleReload}>
                  <SyncIcon className={styles.icon} />
                  Reload
                </Button>
              </div>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} className={styles.dataWrapper}>
          <Grid container gap={"10px"}>
            {loading ? (
              <SkeletonLoading isOpen={true} count={8} height={60} lg={3} />
            ) : (
              <TableContainer component={Paper} className={styles.overViewTable}>
                <Table size="small" aria-label="customer details">
                  <TableBody>
                    <TableRow className={styles.tableRow}>
                      <TableCell className={styles.tableCell} style={{ borderTopLeftRadius: "15px" }}>
                        <span className={styles.tableCellHead}>Name</span>
                        <span className={styles.tableCellValue}>{customer?.name || ""}</span>
                      </TableCell>
                      <TableCell className={styles.tableCell}>
                        <span className={styles.tableCellHead}>E-mail</span>
                        <span className={styles.tableCellValue}>{customer?.email || "-"}</span>
                      </TableCell>
                      <TableCell className={styles.tableCell}>
                        <span className={styles.tableCellHead}>Balance</span>
                        <span className={styles.tableCellValue}>
                          {NumberFormatHelper.customerAmountFormat(
                            hasUnsyncedLogs ? syncedBalance : customer?.balance || 0,
                          )}
                          {hasUnsyncedLogs && (
                            <span style={{ fontSize: "11px", color: "#ff9800", display: "block", marginTop: "4px" }}>
                              Upcoming: {NumberFormatHelper.customerAmountFormat(upcomingBalance)}
                            </span>
                          )}
                        </span>
                      </TableCell>

                      <TableCell className={styles.tableCell} style={{ borderTopRightRadius: "15px" }}>
                        <span className={styles.tableCellHead}>Point</span>
                        <span className={styles.tableCellValue}>{customer?.points || 0}</span>
                      </TableCell>
                    </TableRow>

                    <TableRow className={styles.tableRow}>
                      <TableCell className={styles.tableCell}>
                        <span className={styles.tableCellHead}>Mobile</span>
                        <span className={styles.tableCellValue}>{customer?.mobile || "-"}</span>
                      </TableCell>
                      <TableCell className={styles.tableCell}>
                        <span className={styles.tableCellHead}>Location</span>
                        <span className={styles.tableCellValue}>{customer?.location || "-"}</span>
                      </TableCell>
                      <TableCell className={styles.tableCell}>
                        <span className={styles.tableCellHead}>Credit Limit</span>
                        <span className={styles.tableCellValue}>
                          {NumberFormatHelper.customerAmountFormat(customer?.credit_limit || 0) || 0}
                        </span>
                      </TableCell>
                      <TableCell className={styles.tableCell}>
                        <span className={styles.tableCellHead}>Loyalty Card No</span>
                        <span className={styles.tableCellValue}>{customer?.loyalty_card_no || 0}</span>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <Grid item xs={12}>
              {loading ? (
                <SkeletonLoading isOpen={true} count={12} height={45} xs={12} />
              ) : filteredJournals && filteredJournals.length ? (
                <Paper style={{ borderRadius: "20px 20px" }}>
                  <DataTable columns={headerData} rows={paginatedJournals} rowClickListener={toggleViewSale} />
                  <TablePagination
                    rowsPerPageOptions={[20, 50]}
                    component="div"
                    count={filteredJournals.length}
                    rowsPerPage={customerJournalsPagination.limit}
                    page={customerJournalsPagination.page}
                    onPageChange={handleChangeCustomerJournalPage}
                    onRowsPerPageChange={handleChangeCustomerRowsPerPage}
                  />
                </Paper>
              ) : (
                !loading && (
                  <Info
                    title={"No transactions to list"}
                    content={
                      "You have no transactions to list with current filter configuration. Please clear the filters or make some sale"
                    }
                  />
                )
              )}
            </Grid>
          </Grid>
        </Grid>
        {paymentTab && (
          <AddPaymentToCustomerDialog
            onClose={togglePaymentTab}
            addPaymentToCustomer={addPaymentToCustomerHandler}
            currentBalance={customer?.balance || 0}
            customer={customer}
          />
        )}
        {selectedSaleId && <CustomerSaleDialog closeDialog={() => setSelectedSaleId(null)} saleId={selectedSaleId} />}
        {editCustomer && (
          <EditCustomer
            closeDialog={() => setEditCustomer(false)}
            editCustomerHandler={handleEditCustomer}
            customer={customer}
            onClose={toggleEditCustomerTab}
          />
        )}
      </Grid>
    </>
  );
}

export default CustomerDetails;
